import { lazy, Suspense } from 'react'
import Encabezado from './components/Encabezado'
import PiePagina from './components/PiePagina'
import BotonWhatsappFlotante from './components/BotonWhatsappFlotante'
import AvisoSesionExpirada from './components/AvisoSesionExpirada'
import Hero from './sections/Hero'
import Catalogo from './sections/Catalogo'
import SobreLaMarca from './sections/SobreLaMarca'
import Galeria from './sections/Galeria'
import Cuidados from './sections/Cuidados'
import Envios from './sections/Envios'
import Contacto from './sections/Contacto'
import { useAnclaInicial } from './hooks/useAnclaInicial'
import { useRuta } from './hooks/useRuta'

// Todo lo que no es la portada se carga bajo demanda: quien visita "/" (el
// caso común) nunca paga el peso del panel admin, la wiki de educación o el
// formulario de reseñas. Cada import() es su propio chunk, separado del
// bundle inicial que ya advertía Vite por superar 500 kB.
const PaginaProducto = lazy(() => import('./paginas/PaginaProducto'))
const PaginaPerfil = lazy(() => import('./paginas/PaginaPerfil'))
const PaginaPrivacidad = lazy(() => import('./paginas/PaginaPrivacidad'))
const PaginaEntregaGarantia = lazy(() => import('./paginas/PaginaEntregaGarantia'))
const PaginaPreguntasFrecuentes = lazy(() => import('./paginas/PaginaPreguntasFrecuentes'))
const PaginaAdmin = lazy(() => import('./paginas/PaginaAdmin'))
const PaginaEducacion = lazy(() => import('./paginas/PaginaEducacion'))
const PaginaLeccionEducacion = lazy(() => import('./paginas/PaginaLeccionEducacion'))
const PaginaQuienesSomos = lazy(() => import('./paginas/PaginaQuienesSomos'))

// Las reseñas de producto no son testimonios inventados: viven en Firestore
// y solo se muestran las que alguien aprobó a mano. Ver ResenasProducto.jsx.
const PATRON_PRODUCTO = /^\/producto\/([^/]+)\/?$/
const PATRON_LECCION = /^\/educacion\/([^/]+)\/([^/]+)\/?$/
const RUTA_PERFIL = '/perfil'
const RUTA_PRIVACIDAD = '/privacidad'
const RUTA_ENTREGA = '/entrega-y-garantia'
const RUTA_ADMIN = '/admin'
const RUTA_EDUCACION = '/educacion'
const RUTA_PREGUNTAS_FRECUENTES = '/preguntas-frecuentes'
const RUTA_QUIENES_SOMOS = '/quienes-somos'

function Pagina({
  idProducto,
  esPerfil,
  esPrivacidad,
  esEntrega,
  esAdmin,
  esEducacion,
  esPreguntasFrecuentes,
  esQuienesSomos,
  leccion,
}) {
  if (idProducto) return <PaginaProducto id={idProducto} />
  if (esPerfil) return <PaginaPerfil />
  if (esPrivacidad) return <PaginaPrivacidad />
  if (esEntrega) return <PaginaEntregaGarantia />
  if (esPreguntasFrecuentes) return <PaginaPreguntasFrecuentes />
  if (esQuienesSomos) return <PaginaQuienesSomos />
  if (esAdmin) return <PaginaAdmin />
  if (leccion) return <PaginaLeccionEducacion categoriaSlug={leccion.categoriaSlug} leccionSlug={leccion.leccionSlug} />
  if (esEducacion) return <PaginaEducacion />
  return (
    <>
      <Hero />
      <Catalogo />
      <SobreLaMarca />
      <Galeria />
      <Cuidados />
      <Envios />
      <Contacto />
    </>
  )
}

function App() {
  const ruta = useRuta()
  const idProducto = PATRON_PRODUCTO.exec(ruta)?.[1]
  const matchLeccion = PATRON_LECCION.exec(ruta)
  const leccion = matchLeccion ? { categoriaSlug: matchLeccion[1], leccionSlug: matchLeccion[2] } : null
  const esPerfil = ruta === RUTA_PERFIL
  const esPrivacidad = ruta === RUTA_PRIVACIDAD
  const esEntrega = ruta === RUTA_ENTREGA
  const esAdmin = ruta === RUTA_ADMIN
  const esEducacion = !leccion && ruta === RUTA_EDUCACION
  const esPreguntasFrecuentes = ruta === RUTA_PREGUNTAS_FRECUENTES
  const esQuienesSomos = ruta === RUTA_QUIENES_SOMOS
  const esInicio =
    !idProducto &&
    !esPerfil &&
    !esPrivacidad &&
    !esEntrega &&
    !esAdmin &&
    !esEducacion &&
    !esPreguntasFrecuentes &&
    !esQuienesSomos &&
    !leccion

  useAnclaInicial(esInicio)

  return (
    <>
      <a
        href="#inicio"
        className="sr-only rounded-sm bg-oro px-4 py-2 text-noche focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50"
      >
        Saltar al contenido
      </a>

      <Encabezado />
      <AvisoSesionExpirada />

      <main id="inicio">
        <Suspense
          fallback={
            <div className="flex min-h-screen items-center justify-center pt-(--altura-encabezado) text-sm text-marfil-suave/70">
              Cargando…
            </div>
          }
        >
          <Pagina
            idProducto={idProducto}
            esPerfil={esPerfil}
            esPrivacidad={esPrivacidad}
            esEntrega={esEntrega}
            esAdmin={esAdmin}
            esEducacion={esEducacion}
            esPreguntasFrecuentes={esPreguntasFrecuentes}
            esQuienesSomos={esQuienesSomos}
            leccion={leccion}
          />
        </Suspense>
      </main>

      <PiePagina />

      {/* Fuera del panel admin: es un canal de contacto para clientes, no
          para quien ya está administrando el catálogo. */}
      {!esAdmin && <BotonWhatsappFlotante />}
    </>
  )
}

export default App
